import { ORANGE_TO_ORANGE_PROFILE } from '@guard-dog/contracts/qualification';
import { type FormEvent, useRef, useState } from 'react';
import {
  ApiError,
  api,
  type ConsoleSite,
  type EdgeMode,
  type HostnameQualificationReport,
} from '../api/client';
import { canMoveToEdgeMode } from '../lib/entitlement';

const MODES: EdgeMode[] = ['monitor', 'enforce', 'bypass'];

/**
 * Start with the protected hostname, but never guess an origin. A customer-facing hostname can
 * become the edge route after cutover; saving it as its own origin would then create a loop. The
 * qualification flow therefore requires the operator to name and prove a separate site-serving
 * origin explicitly.
 */
function edgeDefaults(url: string): { hostname: string; origin: string } {
  try {
    const u = new URL(url);
    return { hostname: u.hostname, origin: '' };
  } catch {
    return { hostname: '', origin: '' };
  }
}

type EvidenceState = 'confirmed' | 'unavailable' | 'unknown';
type ProxyLayer = 'none' | 'documented' | 'conflict' | 'unknown';

const EVIDENCE_OPTIONS: { value: EvidenceState; label: string }[] = [
  { value: 'unknown', label: 'Not confirmed' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'unavailable', label: 'Unavailable' },
];

function EvidenceOptions() {
  return EVIDENCE_OPTIONS.map(({ value, label }) => (
    <option key={value} value={value}>
      {label}
    </option>
  ));
}

export function QualificationResult({ report }: { report: HostnameQualificationReport }) {
  const result = report.guardDogCloud.result;
  return (
    <section className={`qualification-result qualification-${result}`} aria-live="polite">
      <p className="ok">
        <strong>Guard Dog is available for this WordPress site.</strong>
      </p>
      {result === 'qualifies' ? (
        <p>
          <strong>Guard Dog Cloud qualifies</strong> for hosted protection on{' '}
          <code>{report.hostname.normalized}</code>. No edge or DNS change has been made.
        </p>
      ) : (
        <>
          <p>
            <strong>
              Guard Dog Cloud{' '}
              {result === 'changes_required' ? 'needs changes' : 'needs manual review'}
            </strong>{' '}
            before hosted protection can cover <code>{report.hostname.normalized}</code>.
          </p>
          {report.guardDogCloud.reasons.length > 0 && (
            <>
              <p className="qualification-heading">Why</p>
              <ul>
                {report.guardDogCloud.reasons.map((reason) => (
                  <li key={reason.code}>{reason.message}</li>
                ))}
              </ul>
            </>
          )}
          {report.guardDogCloud.unlockSteps.length > 0 && (
            <>
              <p className="qualification-heading">What unlocks it</p>
              <ul>
                {report.guardDogCloud.unlockSteps.map((step) => (
                  <li key={step.code}>{step.message}</li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      <p className="muted">
        Checked {report.checkedAt} · DNS {report.evidence.dns.observation.status} · origin{' '}
        {report.evidence.origin.probe.outcome}
        {report.evidence.origin.probe.status !== null
          ? ` (HTTP ${report.evidence.origin.probe.status})`
          : ''}
      </p>
    </section>
  );
}

/**
 * Put a site ON the hosted edge for the first time. Until now this was step 3 of a runbook curl,
 * which was survivable while every edge site kept its original row — but re-enrolling a site mints
 * a NEW site id with empty edge columns (ADR-0022), so an owner who reconnects a protected site
 * had no way back to enforcement. `faustdesignkompanie.com` hit exactly that on 2026-07-27.
 */
function EdgeOnboardForm({
  site,
  canAddProtection,
  onChanged,
}: {
  site: ConsoleSite;
  canAddProtection: boolean;
  onChanged: () => void;
}) {
  const defaults = edgeDefaults(site.url);
  const [hostname, setHostname] = useState(defaults.hostname);
  const [origin, setOrigin] = useState(defaults.origin);
  const [dnsControl, setDnsControl] = useState<EvidenceState>('unknown');
  const [apexStrategy, setApexStrategy] = useState<'flattening' | 'none' | 'unknown'>('unknown');
  const [replacementHostname, setReplacementHostname] = useState('');
  const [originServesSite, setOriginServesSite] = useState<EvidenceState>('unknown');
  const [proxyLayer, setProxyLayer] = useState<ProxyLayer>('unknown');
  const [customerControlsCurrentZone, setCustomerControlsCurrentZone] =
    useState<EvidenceState>('unknown');
  const [canRepointExactHostname, setCanRepointExactHostname] = useState<EvidenceState>('unknown');
  const [directOrigin, setDirectOrigin] = useState<EvidenceState>('unknown');
  const [report, setReport] = useState<HostnameQualificationReport | null>(null);
  // Monitor is the default on purpose: the platform cannot see whether the route exists, so the
  // first write should observe rather than act.
  const [mode, setMode] = useState<EdgeMode>('monitor');
  const [qualifying, setQualifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qualifyError, setQualifyError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const qualificationRevision = useRef(0);

  const enforceBlocked = !canMoveToEdgeMode({ next: mode, canAddProtection });
  const qualifies = report?.guardDogCloud.result === 'qualifies';

  function invalidateQualification() {
    qualificationRevision.current += 1;
    setReport(null);
    setQualifyError(null);
    setSaveError(null);
  }

  if (!open) {
    return (
      <span className="edge-onboard">
        <strong>Guard Dog is available.</strong>{' '}
        <span className="muted">Guard Dog Cloud fit has not been checked.</span>{' '}
        <button type="button" className="link-button" onClick={() => setOpen(true)}>
          Check Guard Dog Cloud fit
        </button>
      </span>
    );
  }

  async function qualify(e: FormEvent) {
    e.preventDefault();
    if (qualifying) return;
    setQualifying(true);
    setQualifyError(null);
    setReport(null);
    const submittedRevision = qualificationRevision.current;
    try {
      const response = await api.qualifyHostname(site.id, {
        hostname: hostname.trim(),
        origin: origin.trim(),
        dnsControl,
        apexStrategy,
        ...(replacementHostname.trim() ? { replacementHostname: replacementHostname.trim() } : {}),
        originServesSite,
        proxyLayer,
        ...(proxyLayer === 'documented'
          ? {
              layeringProfile: ORANGE_TO_ORANGE_PROFILE,
              customerControlsCurrentZone,
              canRepointExactHostname,
              directOrigin,
            }
          : {}),
      });
      // A slow response belongs only to the exact inputs submitted with it. If anything changed
      // while the probe was in flight, discard the stale verdict instead of reopening the write
      // form for evidence the server never checked.
      if (qualificationRevision.current !== submittedRevision) return;
      setReport(response.report);
    } catch (err) {
      if (qualificationRevision.current !== submittedRevision) return;
      setQualifyError(
        err instanceof ApiError && err.status === 429
          ? 'Too many readiness checks. Wait a few minutes and try again.'
          : err instanceof ApiError && err.code === 'unsafe_target'
            ? 'The origin must resolve only to public Internet addresses.'
            : err instanceof ApiError && (err.status === 401 || err.status === 403)
              ? 'Not authorized.'
              : 'Could not complete the readiness check.',
      );
    } finally {
      setQualifying(false);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (saving || enforceBlocked || !qualifies) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.setEdgeMode(site.id, { hostname: hostname.trim(), origin: origin.trim(), mode });
      onChanged();
    } catch (err) {
      setSaveError(
        err instanceof ApiError && err.status === 409
          ? 'That hostname is already mapped to another site that is still active.'
          : err instanceof ApiError && (err.status === 401 || err.status === 403)
            ? 'Not authorized.'
            : 'Could not save the edge configuration.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="edge-onboard-panel">
      <p className="ok">
        <strong>Guard Dog is available for this WordPress site.</strong>
      </p>
      <p className="muted">
        Guard Dog Cloud is optional. This check observes bounded public DNS and HTTP metadata; it
        does not change DNS, Cloudflare, the site, or enforcement.
      </p>
      <form className="form edge-onboard-form" onSubmit={(e) => void qualify(e)}>
        <label>
          Protected hostname
          <input
            value={hostname}
            onChange={(e) => {
              setHostname(e.target.value);
              invalidateQualification();
            }}
            required
          />
        </label>
        <label>
          Separate site-serving origin
          <input
            value={origin}
            placeholder="https://origin.example.com"
            onChange={(e) => {
              setOrigin(e.target.value);
              invalidateQualification();
            }}
            required
          />
        </label>
        <label>
          DNS control
          <select
            value={dnsControl}
            onChange={(e) => {
              setDnsControl(e.target.value as EvidenceState);
              invalidateQualification();
            }}
          >
            <EvidenceOptions />
          </select>
        </label>
        <label>
          Origin serves this exact site
          <select
            value={originServesSite}
            onChange={(e) => {
              setOriginServesSite(e.target.value as EvidenceState);
              invalidateQualification();
            }}
          >
            <EvidenceOptions />
          </select>
        </label>
        <label>
          Existing proxy or CDN
          <select
            value={proxyLayer}
            onChange={(e) => {
              setProxyLayer(e.target.value as ProxyLayer);
              invalidateQualification();
            }}
          >
            <option value="unknown">Not confirmed</option>
            <option value="none">None</option>
            <option value="documented">Customer-controlled Cloudflare orange cloud</option>
            <option value="conflict">Other or conflicting full proxy</option>
          </select>
        </label>
        <label>
          Apex DNS support (only if the protected hostname is an apex)
          <select
            value={apexStrategy}
            onChange={(e) => {
              setApexStrategy(e.target.value as 'flattening' | 'none' | 'unknown');
              invalidateQualification();
            }}
          >
            <option value="unknown">Not confirmed / not an apex</option>
            <option value="flattening">Flattening or ALIAS confirmed</option>
            <option value="none">Not available</option>
          </select>
        </label>
        <label>
          Replacement hostname (optional)
          <input
            value={replacementHostname}
            placeholder="www.example.com"
            onChange={(e) => {
              setReplacementHostname(e.target.value);
              invalidateQualification();
            }}
          />
        </label>
        {proxyLayer === 'documented' && (
          <fieldset className="qualification-authority">
            <legend>Orange-to-orange control</legend>
            <p className="muted">
              The reviewed profile requires all three facts for this exact hostname.
            </p>
            <label>
              Customer controls the current Cloudflare zone
              <select
                value={customerControlsCurrentZone}
                onChange={(e) => {
                  setCustomerControlsCurrentZone(e.target.value as EvidenceState);
                  invalidateQualification();
                }}
              >
                <EvidenceOptions />
              </select>
            </label>
            <label>
              Customer can repoint the exact hostname
              <select
                value={canRepointExactHostname}
                onChange={(e) => {
                  setCanRepointExactHostname(e.target.value as EvidenceState);
                  invalidateQualification();
                }}
              >
                <EvidenceOptions />
              </select>
            </label>
            <label>
              Separate direct origin exists
              <select
                value={directOrigin}
                onChange={(e) => {
                  setDirectOrigin(e.target.value as EvidenceState);
                  invalidateQualification();
                }}
              >
                <EvidenceOptions />
              </select>
            </label>
          </fieldset>
        )}
        {qualifyError && <span className="error">{qualifyError}</span>}
        <div className="edge-onboard-actions">
          <button type="submit" disabled={qualifying}>
            {qualifying ? 'Checking…' : report ? 'Check again' : 'Check fit'}
          </button>
          <button
            type="button"
            className="link-button"
            onClick={() => {
              invalidateQualification();
              setOpen(false);
            }}
            disabled={qualifying || saving}
          >
            Cancel
          </button>
        </div>
      </form>

      {report && <QualificationResult report={report} />}

      {qualifies && (
        <form className="form edge-config-form" onSubmit={(e) => void save(e)}>
          <h4>Prepare edge configuration</h4>
          <p className="muted">
            Qualified target: <code>{hostname.trim()}</code> → <code>{origin.trim()}</code>
          </p>
          <label>
            Initial mode
            <select value={mode} onChange={(e) => setMode(e.target.value as EdgeMode)}>
              {MODES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <p className="muted">
            Saving records the configuration only. Route attachment and DNS cutover remain separate
            operator steps. Monitor is the safe default.
          </p>
          {enforceBlocked && (
            <span className="restriction-note">Enforce paused until plan active.</span>
          )}
          {saveError && <span className="error">{saveError}</span>}
          <button type="submit" disabled={saving || enforceBlocked}>
            {saving ? 'Saving…' : 'Save edge configuration'}
          </button>
        </form>
      )}
    </div>
  );
}

/**
 * The site's enforcement mode. Owner/admin can switch it (PUT /sites/:id/edge re-sends the stored
 * hostname/origin with the new mode); everyone else sees it read-only. Not shown off the edge.
 */
export function EdgeControl({
  site,
  canEdit,
  canAddProtection,
  onChanged,
}: {
  site: ConsoleSite;
  canEdit: boolean;
  canAddProtection: boolean;
  onChanged: () => void;
}) {
  const [pending, setPending] = useState<EdgeMode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const hostname = site.edgeHostname;
  const origin = site.edgeOrigin;
  const current: EdgeMode = site.edgeMode ?? 'monitor';
  const enforcePaused = !canMoveToEdgeMode({
    current,
    next: 'enforce',
    canAddProtection,
  });

  if (!hostname || !origin) {
    if (!canEdit) return <p className="muted">Not on the hosted edge.</p>;
    return (
      <EdgeOnboardForm site={site} canAddProtection={canAddProtection} onChanged={onChanged} />
    );
  }
  if (!canEdit) return <span className={`badge badge-${current}`}>{current}</span>;

  const edge = { hostname, origin }; // narrowed to strings here; captured for the async closure

  async function change(mode: EdgeMode) {
    if (mode === current || pending) return;
    if (!canMoveToEdgeMode({ current, next: mode, canAddProtection })) return;
    setPending(mode);
    setError(null);
    try {
      await api.setEdgeMode(site.id, { ...edge, mode });
      onChanged();
    } catch (e) {
      setError(
        e instanceof ApiError && (e.status === 401 || e.status === 403)
          ? 'Not authorized.'
          : 'Failed to change mode.',
      );
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="edge-control">
      <fieldset className="segmented">
        <legend className="sr-only">Enforcement mode</legend>
        {MODES.map((m) => (
          <button
            key={m}
            type="button"
            className={m === current ? 'active' : ''}
            disabled={
              pending !== null ||
              m === current ||
              !canMoveToEdgeMode({ current, next: m, canAddProtection })
            }
            title={
              m === 'enforce' && enforcePaused
                ? 'Moving edge protection to enforce is paused until the plan is active.'
                : undefined
            }
            onClick={() => change(m)}
          >
            {pending === m ? '…' : m}
          </button>
        ))}
      </fieldset>
      {error && <span className="error">{error}</span>}
      {enforcePaused && <span className="restriction-note">Enforce paused until plan active.</span>}
    </div>
  );
}
